export type GridSegment = { start: number; size: number };

/**
 * Splits `total` fine units into contiguous segments sized by `weights`, distributing
 * the rounding remainder to the largest fractional parts so the axis is filled exactly
 * (sum of sizes === total). Equal weights reduce to an even split. All-zero weights are
 * treated as equal.
 */
export const splitByWeights = (total: number, weights: readonly number[]): GridSegment[] => {
  const count = weights.length;
  if (count === 0) return [];

  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  const effectiveWeights = weightSum > 0 ? weights : weights.map(() => 1);
  const effectiveSum = weightSum > 0 ? weightSum : count;

  const idealSizes = effectiveWeights.map(weight => (total * weight) / effectiveSum);
  const sizes = idealSizes.map(Math.floor);
  const usedUnits = sizes.reduce((sum, size) => sum + size, 0);
  const remainder = total - usedUnits;

  const byLargestFraction = idealSizes
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  for (let i = 0; i < remainder; i++) {
    sizes[byLargestFraction[i % count].index] += 1;
  }

  const segments: GridSegment[] = [];
  let start = 0;
  for (let i = 0; i < count; i++) {
    segments.push({ start, size: sizes[i] });
    start += sizes[i];
  }
  return segments;
};
